import React, {useEffect, useState} from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {FixedSizeList} from 'react-window';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import {fade, makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import JString from '@easytech-international-sdn-bhd/jstring';
import Checkbox from '@material-ui/core/Checkbox';

const useStyles = makeStyles(theme => ({
	paper: {
		borderRadius: 12,
		overflow: 'hidden',
	},
	dialogTitle: {
		padding: theme.spacing(3, 3, 2, 3),
	},
	titleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: theme.spacing(2),
	},
	titlePrimary: {
		fontSize: '1.25rem',
		fontWeight: 600,
		lineHeight: 1.3,
		color: theme.palette.text.primary,
	},
	titleSubtitle: {
		fontSize: '0.8125rem',
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
	},
	segmented: {
		display: 'inline-flex',
		backgroundColor: fade(theme.palette.text.primary, 0.06),
		borderRadius: 8,
		padding: 2,
		flexShrink: 0,
	},
	segment: {
		minWidth: 80,
		color: theme.palette.text.secondary,
		fontWeight: 500,
		textTransform: 'none',
		borderRadius: 6,
		padding: theme.spacing(0.5, 1.5),
		'&:hover': {backgroundColor: 'transparent'},
	},
	segmentActive: {
		minWidth: 80,
		color: '#000',
		backgroundColor: theme.palette.primary.main,
		fontWeight: 700,
		textTransform: 'none',
		borderRadius: 6,
		padding: theme.spacing(0.5, 1.5),
		boxShadow: `0 2px 6px ${fade(theme.palette.primary.main, 0.4)}`,
		'&:hover': {
			backgroundColor: theme.palette.primary.main,
		},
	},
	searchRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		marginTop: theme.spacing(2),
	},
	search: {
		position: 'relative',
		flex: 1,
		borderRadius: 12,
		backgroundColor: fade(theme.palette.text.primary, 0.04),
		border: `1px solid ${fade(theme.palette.text.primary, 0.08)}`,
		transition: theme.transitions.create(
			['background-color', 'box-shadow', 'border-color'],
			{duration: 200}
		),
		'&:hover': {
			borderColor: fade(theme.palette.text.primary, 0.16),
		},
		'&:focus-within': {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.16)}`,
		},
	},
	searchIcon: {
		padding: theme.spacing(0, 1.5),
		height: '100%',
		position: 'absolute',
		pointerEvents: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: theme.palette.text.secondary,
	},
	inputRoot: {
		color: theme.palette.text.primary,
		width: '100%',
	},
	inputInput: {
		padding: theme.spacing(1.25, 1, 1.25, 0),
		paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
		fontSize: '0.9375rem',
		width: '100%',
	},
	checkAllBtn: {
		flexShrink: 0,
		textTransform: 'none',
		fontWeight: 600,
		color: theme.palette.text.primary,
		borderRadius: 8,
		padding: theme.spacing(1, 1.5),
		'&:hover': {
			backgroundColor: fade(theme.palette.text.primary, 0.06),
		},
	},
	titleLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		flexWrap: 'wrap',
	},
	selectionBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: theme.spacing(0.25, 1),
		borderRadius: 999,
		backgroundColor: fade(theme.palette.primary.main, 0.16),
		color: theme.palette.type === 'light' ? '#7a6f00' : theme.palette.primary.main,
		fontSize: '0.6875rem',
		fontWeight: 700,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
	},
	dialogContent: {
		padding: 0,
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	listItem: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		cursor: 'pointer',
		transition: theme.transitions.create('background-color', {duration: 150}),
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	listItemSelected: {
		backgroundColor: fade(theme.palette.primary.main, 0.08),
		'&:hover': {
			backgroundColor: fade(theme.palette.primary.main, 0.12),
		},
	},
	listItemPrimary: {
		fontSize: '0.9375rem',
		fontWeight: 500,
		color: theme.palette.text.primary,
	},
	listItemPrimarySelected: {
		fontWeight: 600,
	},
	listItemSecondary: {
		fontSize: '0.8125rem',
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: theme.spacing(6, 2),
		gap: theme.spacing(1),
		height: 400,
	},
	emptyIcon: {
		fontSize: 56,
		color: theme.palette.text.disabled,
		opacity: 0.4,
		marginBottom: theme.spacing(1),
	},
	emptyTitle: {
		fontSize: '1rem',
		fontWeight: 600,
		color: theme.palette.text.secondary,
	},
	emptySubtitle: {
		fontSize: '0.8125rem',
		color: theme.palette.text.disabled,
	},
	scrollContainer: {
		'&::-webkit-scrollbar': {width: 8},
		'&::-webkit-scrollbar-track': {backgroundColor: 'transparent'},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: fade(theme.palette.text.primary, 0.2),
			borderRadius: 4,
			'&:hover': {
				backgroundColor: fade(theme.palette.text.primary, 0.32),
			},
		},
		scrollbarWidth: 'thin',
		scrollbarColor: `${fade(theme.palette.text.primary, 0.2)} transparent`,
	},
	actions: {
		padding: theme.spacing(2, 3),
		gap: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	cancelBtn: {
		textTransform: 'none',
		fontWeight: 500,
		borderRadius: 8,
	},
	doneBtn: {
		textTransform: 'none',
		fontWeight: 600,
		borderRadius: 8,
		boxShadow: 'none',
		'&:hover': {
			boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.32)}`,
		},
	},
	doneCount: {
		marginLeft: theme.spacing(0.5),
		fontWeight: 700,
	},
	spacer: {flex: 1},
	checkbox: {
		padding: theme.spacing(1),
	},
}));

export default function VirtualListCB2({setClose, open, title, data, language, limit, filteration, selected, enabledCheckAll = true}) {
	const classes = useStyles();
	const [dataArr, setDataArr] = useState([]);
	const [checkedList, setCheckedList] = useState({});
	const [confirmedList, setConfirmedList] = useState({});
	const [listReturn, setListReturn] = useState([]);
	const [status, setStatus] = useState(1);
	const [searchText, setSearchText] = useState('');

	useEffect(() => {
		if (data && data.length > 0) {
			if (filteration) {
				setDataArr(dataArr => (data.filter(item => item.status === status)));
			} else {
				setDataArr(dataArr => (data));
			}
		}
	}, [open, data, status]);

	useEffect(() => {
		if (open) {
			if (selected && selected.length > 0) {
				var tmp = Object.assign({});
				for (var i = 0; i < selected.length; i++) {
					const {value} = selected[i];
					tmp[`${value}`] = selected[i];
				}
				setCheckedList(tmp);
				setConfirmedList(tmp);
			} else {
				setCheckedList(confirmedList);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, selected]);

	const onSelectChange = (_cur) => {
		const {value} = _cur;
		var tmp = Object.assign({}, checkedList);
		if (value in tmp) {
			delete tmp[`${value}`];
		} else {
			tmp[`${value}`] = _cur;
		}
		setCheckedList(tmp);
	};

	const checkAll = () => {
		var tmp = Object.assign({}, checkedList);
		for (var i = 0; i < dataArr.length; i++) {
			const {value, hide} = dataArr[i];
			if (hide === false || hide === undefined) {
				if (value in tmp) {
					delete tmp[`${value}`];
				} else {
					tmp[`${value}`] = dataArr[i];
				}
			}
		}
		setCheckedList(tmp);
	};

	const savecustomer = () => {
		const list = Object.values(checkedList);
		var selectedData = [];

		for (var i = 0; i < list.length; i++) {
			selectedData.push({
				name: list[i].name,
				value: list[i].value,
				company: list[i].company
			});
		}
		if (filteration) {
			setDataArr(dataArr => (data.filter(item => item.status === status)));
		} else {
			setDataArr(data);
		}
		setConfirmedList(checkedList);
		setListReturn(selectedData);
		setClose(selectedData);
	};

	const closeReset = () => {
		setStatus(1);
		setListReturn([]);
		setSearchText('');
	};

	const onChangeSearchText = (event) => {
		var value = event.target.value;
		setSearchText(value);
		value = String(value).split('?').join('').split('[').join('').split(']').join('').split('(').join('').split(')').join('');
		var text = JString(value);
		var final = [];
		var search = (text.str).split(' ');
		if (text.isValid()) {
			for (let idx = 0; idx < data.length; idx++) {
				var count = 0;
				const row = data[idx];
				var searchString = row.value + row.name;
				for (let i = 0; i < search.length; i++) {
					var isFound = JString(searchString).searchDeep(search[i]);
					if ((isFound && filteration && row.status === status) || (isFound && !filteration)) {
						count++;
					}
				}
				if (count === search.length) {
					final.push(row);
				}
			}
			setDataArr(final);
		} else {
			if (filteration) {
				setDataArr(dataArr => (data.filter(item => item.status === status)));
			} else {
				setDataArr(data);
			}
		}
	};

	const RenderRow = ({props}) => {
		const {index, style} = props;
		const _cur = dataArr[index];
		const isChecked = _cur && (_cur.value in checkedList);
		if (index !== dataArr.length) {
			return (
				<ListItem
					button
					style={style}
					key={index}
					disabled={_cur?.hide === true}
					onClick={() => _cur?.hide ? null : onSelectChange(_cur)}
					className={`${classes.listItem} ${isChecked ? classes.listItemSelected : ''}`}>
					<Checkbox
						id={_cur?.value}
						className={classes.checkbox}
						disabled={_cur?.hide === true}
						tabIndex={-1}
						disableRipple
						inputProps={{'aria-labelledby': String(index)}}
						checked={isChecked}
						color="primary"
					/>
					<ListItemText
						primary={_cur?.name}
						secondary={_cur?.description != null ? (
							<>
								<span>{_cur?.description}</span>
								<span style={{display: 'block'}}>{_cur?.value}</span>
							</>
						) : _cur?.secondary}
						primaryTypographyProps={{
							className: `${classes.listItemPrimary} ${isChecked ? classes.listItemPrimarySelected : ''}`,
							style: {textDecorationLine: _cur?.hide ? 'line-through' : undefined},
						}}
						secondaryTypographyProps={{
							className: classes.listItemSecondary,
							style: {textDecorationLine: _cur?.hide ? 'line-through' : undefined},
						}}
					/>
				</ListItem>
			);
		}
		return null;
	};

	const selectionCount = Object.keys(checkedList).length;
	const doneDisabled = (typeof (limit) !== 'undefined' && limit > 0) && (selectionCount > limit);

	return (
		<Dialog
			fullScreen={false}
			maxWidth="sm"
			fullWidth
			open={open}
			onClose={() => {}}
			PaperProps={{className: classes.paper}}
			aria-labelledby="max-width-dialog-title">
			<DialogTitle id="max-width-dialog-title" className={classes.dialogTitle} disableTypography>
				<div className={classes.titleRow}>
					<div className={classes.titleLeft}>
						<Typography component="div" className={classes.titlePrimary}>
							{title}
						</Typography>
						<div className={classes.selectionBadge}>
							{selectionCount} {language.selected}
						</div>
					</div>
					{filteration ? (
						<div className={classes.segmented}>
							<Button
								className={status === 1 ? classes.segmentActive : classes.segment}
								onClick={() => setStatus(1)}
								size="small"
								disableRipple>
								{language.active}
							</Button>
							<Button
								className={status === 0 ? classes.segmentActive : classes.segment}
								onClick={() => setStatus(0)}
								size="small"
								disableRipple>
								{language.inactive}
							</Button>
						</div>
					) : null}
				</div>
				<div className={classes.searchRow}>
					<div className={classes.search}>
						<div className={classes.searchIcon}>
							<SearchRoundedIcon/>
						</div>
						<InputBase
							value={searchText}
							placeholder={language.search}
							classes={{root: classes.inputRoot, input: classes.inputInput}}
							inputProps={{'aria-label': 'search'}}
							onChange={onChangeSearchText}
						/>
					</div>
					{enabledCheckAll ? (
						<Button onClick={() => checkAll()} size="small" className={classes.checkAllBtn}>
							{language.check_all}
						</Button>
					) : null}
				</div>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{dataArr.length === 0 ? (
					<div className={classes.emptyState}>
						<SearchRoundedIcon className={classes.emptyIcon}/>
						<Typography className={classes.emptyTitle}>
							{language.no_matches_found}
						</Typography>
						<Typography className={classes.emptySubtitle}>
							{language.try_different_keyword}
						</Typography>
					</div>
				) : (
					<FixedSizeList
						height={400}
						width="100%"
						itemSize={56}
						itemCount={dataArr.length}
						className={classes.scrollContainer}>
						{children => <RenderRow props={children}/>}
					</FixedSizeList>
				)}
			</DialogContent>
			<DialogActions className={classes.actions}>
				<div className={classes.spacer}/>
				<Button
					variant="outlined"
					className={classes.cancelBtn}
					onClick={() => {
						setClose(false);
						closeReset();
					}}>
					{language.cancel}
				</Button>
				<Button
					variant="contained"
					color="primary"
					className={classes.doneBtn}
					disabled={doneDisabled || selectionCount === 0}
					onClick={() => {
						savecustomer();
						closeReset();
					}}>
					{language.done}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
